import React from "react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function smoothPrices(arr) {
  return arr.map((p, i) => {
    let sum = 0, count = 0;
    for (let j = i - 1; j <= i + 1; j++) {
      if (arr[j]) { sum += arr[j].price; count++; }
    }
    return { ...p, price: sum / count };
  });
}

function useCountUp(target, duration = 1400) {
  const [value, setValue] = React.useState(0);
  const raf   = React.useRef(null);
  const from  = React.useRef(0);
  const start = React.useRef(null);

  React.useEffect(() => {
    if (target === 0) { setValue(0); return; }
    from.current  = value;
    start.current = null;
    const animate = (ts) => {
      if (!start.current) start.current = ts;
      const p     = Math.min((ts - start.current) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from.current + (target - from.current) * eased));
      if (p < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);

  return value;
}

// ── Booth counter API helpers ─────────────────────────────────────────────────
const BOOTH_API = (import.meta.env.VITE_API_BASE_URL || "") + "/public/booth-counter";

async function getBoothCounter() {
  try {
    const res = await fetch(BOOTH_API);
    if (!res.ok) return { total: 0, count: 0 };
    const data = await res.json();
    return { total: data.total_sats || 0, count: data.demo_count || 0 };
  } catch { return { total: 0, count: 0 }; }
}

async function addToConferenceTotal(sats) {
  try {
    const res = await fetch(BOOTH_API + "/add", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sats }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return { total: data.total_sats, count: data.demo_count };
  } catch { return null; }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CADENCES = [
  { value: "daily",     label: "Daily",     sub: "Every day"     },
  { value: "weekly",    label: "Weekly",    sub: "Every week"    },
  { value: "bi-weekly", label: "Bi-weekly", sub: "Every 2 weeks" },
];

const PRESETS = [10, 25, 50, 100, 250, 500];

// ─── Conference counter banner ────────────────────────────────────────────────

function ConferenceBanner({ total, count }) {
  const animated = useCountUp(total, 2000);
  return (
    <div style={{
      background: "linear-gradient(135deg, #1c1917 0%, #0c0a09 100%)",
      borderBottom: "1px solid #292524",
      padding: "10px 16px",
    }}>
      {/* Row 1: live dot + event label */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: "#f97316",
          display: "inline-block",
          boxShadow: "0 0 6px #f97316",
          animation: "pulse 2s infinite",
          flexShrink: 0,
        }} />
        <span style={{ fontSize: "0.7rem", color: "#78716c", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          Bitcoin 2026 · Booth #3391
        </span>
      </div>

      {/* Row 2: sats number + label */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, paddingLeft: 16 }}>
        <span style={{ color: "#fb923c", fontWeight: 700, fontSize: "0.9rem", fontVariantNumeric: "tabular-nums" }}>
          {animated.toLocaleString()}
        </span>
        <span style={{ fontSize: "0.75rem", color: "#a8a29e" }}>sats left on the table</span>
      </div>

      {/* Row 3: people count */}
      {count > 0 && (
        <div style={{ paddingLeft: 16, marginTop: 2 }}>
          <span style={{ fontSize: "0.68rem", color: "#57534e" }}>
            {count} {count !== 1 ? "people" : "person"} calculated so far
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, highlight }) {
  return (
    <div style={{
      background: "#18181b", border: "1px solid #27272a",
      borderRadius: 12, padding: "14px 16px",
    }}>
      <div style={{ fontSize: "0.7rem", color: "#71717a", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: highlight ? "#fb923c" : "#e4e4e7" }}>
        {value}
        <span style={{ fontSize: "0.7rem", fontWeight: 400, marginLeft: 4, color: highlight ? "#c2410c" : "#52525b" }}>{sub}</span>
      </div>
    </div>
  );
}

// ─── Tranche breakdown ────────────────────────────────────────────────────────

function TrancheBreakdown({ windows }) {
  const counts = React.useMemo(() => {
    const c = { early_gap: 0, tranche: 0, failsafe: 0 };
    for (const w of (windows || [])) {
      for (const b of (w.buys || [])) {
        if (b.label === "early_gap") c.early_gap++;
        else if (b.label === "failsafe" || b.label === "end_of_window") c.failsafe++;
        else c.tranche++;
      }
    }
    return c;
  }, [windows]);

  const total = counts.early_gap + counts.tranche + counts.failsafe;
  if (total === 0) return null;

  const items = [
    { label: "Early gap buy", count: counts.early_gap, color: "#fb923c" },
    { label: "Dip tranche",   count: counts.tranche,   color: "#f97316" },
    { label: "Failsafe",      count: counts.failsafe,  color: "#52525b" },
  ].filter(i => i.count > 0);

  return (
    <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: "0.7rem", color: "#71717a", marginBottom: 10 }}>How buys were triggered</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map(({ label, count, color }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, width: 110, flexShrink: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
              <span style={{ fontSize: "0.7rem", color: "#a1a1aa" }}>{label}</span>
            </div>
            <div style={{ flex: 1, background: "#27272a", borderRadius: 999, height: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", background: color, borderRadius: 999, width: `${Math.round((count / total) * 100)}%` }} />
            </div>
            <span style={{ fontSize: "0.7rem", color: "#71717a", width: 20, textAlign: "right" }}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main kiosk component ─────────────────────────────────────────────────────

export default function KioskCalculator() {
  const [cadence,         setCadence]         = React.useState("bi-weekly");
  const [buyUsd,          setBuyUsd]          = React.useState(100);
  const [isCustom,        setIsCustom]        = React.useState(false);
  const [customVal,       setCustomVal]       = React.useState("");
  const [result,          setResult]          = React.useState(null);
  const [loading,         setLoading]         = React.useState(false);
  const [err,             setErr]             = React.useState(null);
  const [hasRun,          setHasRun]          = React.useState(false);
  const [conferenceTotal, setConferenceTotal] = React.useState(0);
  const [demoCount,       setDemoCount]       = React.useState(0);
  const [addedToTotal,    setAddedToTotal]    = React.useState(false);
  const [biweeklyPct,     setBiweeklyPct]     = React.useState(null);

  const resultRef = React.useRef(null);
  const topRef    = React.useRef(null);

  // Load conference counter on mount
  React.useEffect(() => {
    getBoothCounter().then(({ total, count }) => {
      setConferenceTotal(total);
      setDemoCount(count);
    });
  }, []);

  const effectiveBuy = isCustom ? (Number(customVal) || 0) : buyUsd;

  const monthlyBudget = React.useMemo(() => {
    const a = effectiveBuy;
    if (cadence === "daily")     return a * 30;
    if (cadence === "weekly")    return a * 4;
    return a * 2;
  }, [cadence, effectiveBuy]);

  const onCalculate = async (e) => {
    e.preventDefault();
    if (!effectiveBuy || effectiveBuy <= 0) return;
    setLoading(true);
    setErr(null);
    setAddedToTotal(false);
    setBiweeklyPct(null);
    try {
      const base = import.meta.env.VITE_API_BASE_URL || "";
      const url  = `${base}/public/calculator?cadence=${encodeURIComponent(cadence)}&buy_usd=${encodeURIComponent(effectiveBuy)}`;
      const res  = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResult(data);
      setHasRun(true);
      setAddedToTotal(false);

      requestAnimationFrame(() =>
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );

      // Background fetch to see if bi-weekly beats current cadence
      if (cadence === "daily" || cadence === "weekly") {
        const currentBestPct = data.period_results?.length
          ? Math.max(...data.period_results.map(p => p.extra_pct))
          : data.extra_pct;
        fetch(`${base}/public/calculator?cadence=bi-weekly&buy_usd=${encodeURIComponent(effectiveBuy)}`)
          .then(r => r.ok ? r.json() : null)
          .then(bw => {
            if (!bw) return;
            const bwBestPct = bw.period_results?.length
              ? Math.max(...bw.period_results.map(p => p.extra_pct))
              : bw.extra_pct;
            if (bwBestPct - currentBestPct >= 0.75) {
              setBiweeklyPct(bwBestPct - currentBestPct);
            }
          })
          .catch(() => {});
      }
    } catch (e) {
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    setResult(null);
    setHasRun(false);
    setErr(null);
    setAddedToTotal(false);
    setBiweeklyPct(null);
    addedRef.current = false;
    setCadence("bi-weekly");
    setBuyUsd(100);
    setIsCustom(false);
    setCustomVal("");
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const bestPeriod = result?.period_results?.length
    ? result.period_results.reduce((a, b) => a.extra_pct > b.extra_pct ? a : b)
    : null;

  const bestPeriodStats = React.useMemo(() => {
    if (!result?.period_results || !result?.windows) {
      return {
        extraSats:    result?.extra_sats_total    || 0,
        baselineSats: result?.baseline_sats_total || 0,
        smartSats:    result?.smart_sats_total    || 0,
        numWindows:   result?.num_windows         || 0,
        avgExtraPerWindow: result?.avg_extra_sats_per_window || 0,
      };
    }
    const best = result.period_results.reduce((a, b) => a.extra_pct > b.extra_pct ? a : b);
    const cutoffMs = Date.now() - best.period_days * 86400000;
    const filtered = result.windows.filter(w => new Date(w.window_start).getTime() >= cutoffMs);
    const extraSats    = filtered.reduce((s, w) => s + w.extra_sats, 0);
    const baselineSats = filtered.reduce((s, w) => s + w.baseline_sats, 0);
    const smartSats    = filtered.reduce((s, w) => s + w.smart_sats, 0);
    const numWindows   = filtered.length;
    return {
      extraSats,
      baselineSats,
      smartSats,
      numWindows,
      avgExtraPerWindow: numWindows ? Math.round(extraSats / numWindows) : 0,
    };
  }, [result]);

  const animatedExtra = useCountUp(bestPeriodStats.extraSats);

  // Add to conference counter once bestPeriodStats is computed
  const addedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasRun || !result || addedRef.current) return;
    const extraSats = bestPeriodStats.extraSats;
    if (extraSats <= 0) return;
    addedRef.current = true;
    addToConferenceTotal(extraSats).then(updated => {
      if (updated) {
        setConferenceTotal(updated.total);
        setDemoCount(updated.count);
        setAddedToTotal(true);
      }
    });
  }, [hasRun, bestPeriodStats.extraSats]);

  const periodLabel = bestPeriod?.period_days === 91 ? "3 months"
    : bestPeriod?.period_days === 182 ? "6 months"
    : bestPeriod?.period_days === 273 ? "9 months"
    : "12 months";

  return (
    <div ref={topRef} style={{ minHeight: "100vh", background: "#09090b", color: "#fff", overflowX: "hidden" }}>
      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      `}</style>

      {/* Conference counter banner */}
      <ConferenceBanner total={conferenceTotal} count={demoCount} />

      {/* Header */}
      <div style={{ padding: "32px 20px 20px", maxWidth: 480, margin: "0 auto" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: "#18181b", border: "1px solid #27272a",
          borderRadius: 999, padding: "6px 14px", marginBottom: 20,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: "0.7rem", color: "#71717a", letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Smart Timing · Historical Simulation
          </span>
        </div>

        <h1 style={{ fontSize: "2rem", fontWeight: 800, lineHeight: 1.2, marginBottom: 10 }}>
          Are you leaving sats<br />
          <span style={{ color: "#fb923c" }}>on the table?</span>
        </h1>
        <p style={{ fontSize: "0.85rem", color: "#71717a", lineHeight: 1.6 }}>
          Enter your DCA schedule. We simulate Smart Timing using real Bitcoin price data.
        </p>
      </div>

      {/* Form */}
      <div style={{ padding: "0 20px", maxWidth: 480, margin: "0 auto" }}>
        <div style={{
          background: "#18181b", border: "1px solid #27272a",
          borderRadius: 16, padding: 20, marginBottom: 16,
        }}>
          {/* Cadence */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.65rem", color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
              How often do you buy?
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {CADENCES.map(({ value, label, sub }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCadence(value)}
                  style={{
                    padding: "12px 8px", borderRadius: 12,
                    border: `1px solid ${cadence === value ? "#f97316" : "#27272a"}`,
                    background: cadence === value ? "#f97316" : "#09090b",
                    color: "#fff", cursor: "pointer",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>{label}</span>
                  <span style={{ fontSize: "0.65rem", color: cadence === value ? "#fed7aa" : "#52525b" }}>{sub}</span>
                </button>
              ))}
            </div>

            {(cadence === "daily" || cadence === "weekly") && biweeklyPct !== null && (
              <p style={{ fontSize: "0.7rem", color: "#71717a", marginTop: 10, lineHeight: 1.6 }}>
                💡 Historically, switching to bi-weekly captured ~{(Math.round(biweeklyPct * 2) / 2).toFixed(1).replace(".0", "")}% more sats than your current schedule — longer windows give Smart Timing more room to find dips.{" "}
                <button
                  type="button"
                  style={{ color: "#fb923c", textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem", padding: 0 }}
                  onClick={() => {
                    const biweeklyAmt = Math.round(effectiveBuy * (cadence === "daily" ? 14 : 2));
                    setIsCustom(true);
                    setCustomVal(String(biweeklyAmt));
                    setCadence("bi-weekly");
                  }}
                >
                  Try bi-weekly →
                </button>
              </p>
            )}
          </div>

          {/* Amount */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.65rem", color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
              How much per buy?
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {PRESETS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => { setBuyUsd(amt); setIsCustom(false); setCustomVal(""); }}
                  style={{
                    padding: "6px 14px", borderRadius: 8,
                    border: `1px solid ${!isCustom && buyUsd === amt ? "#f97316" : "#27272a"}`,
                    background: !isCustom && buyUsd === amt ? "#f97316" : "#09090b",
                    color: "#fff", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700,
                    transition: "all 0.15s",
                  }}
                >
                  ${amt}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setIsCustom(true)}
                style={{
                  padding: "6px 14px", borderRadius: 8,
                  border: `1px solid ${isCustom ? "#f97316" : "#27272a"}`,
                  background: isCustom ? "#f97316" : "#09090b",
                  color: "#fff", cursor: "pointer", fontSize: "0.75rem", fontWeight: 700,
                  transition: "all 0.15s",
                }}
              >
                Custom
              </button>
            </div>

            {isCustom && (
              <div style={{
                display: "flex", alignItems: "center",
                background: "#09090b", border: "1px solid #52525b",
                borderRadius: 12, padding: "12px 16px", marginBottom: 8,
              }}>
                <span style={{ color: "#71717a", fontSize: "1.1rem", marginRight: 4 }}>$</span>
                <input
                  autoFocus
                  value={customVal}
                  onChange={(e) => setCustomVal(e.target.value)}
                  inputMode="decimal"
                  placeholder="Enter amount"
                  style={{
                    flex: 1, background: "transparent", color: "#fff",
                    fontSize: "1.2rem", fontWeight: 700, outline: "none", border: "none",
                    minWidth: 0,
                  }}
                />
              </div>
            )}

            {effectiveBuy > 0 && (
              <div style={{ fontSize: "0.7rem", color: "#52525b" }}>
                ≈ <span style={{ color: "#a1a1aa" }}>${Math.round(monthlyBudget).toLocaleString()}/mo</span> total budget
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onCalculate}
            disabled={loading || effectiveBuy <= 0}
            style={{
              width: "100%", padding: "16px", borderRadius: 12,
              background: loading || effectiveBuy <= 0 ? "#7c2d12" : "#f97316",
              color: "#fff", fontWeight: 800, fontSize: "1rem",
              border: "none", cursor: loading || effectiveBuy <= 0 ? "not-allowed" : "pointer",
              opacity: loading || effectiveBuy <= 0 ? 0.5 : 1,
              transition: "all 0.15s",
            }}
          >
            {loading ? "Simulating…" : "Calculate my extra sats →"}
          </button>

          {err && <p style={{ marginTop: 10, fontSize: "0.75rem", color: "#f87171", textAlign: "center" }}>{err}</p>}
        </div>

        {/* Results */}
        <div ref={resultRef} style={{ scrollMarginTop: 16 }}>
          {hasRun && result && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 32, animation: "fadeIn 0.4s ease" }}>

              {/* Hero result card */}
              <div style={{
                background: "#18181b", border: "1px solid rgba(249,115,22,0.3)",
                borderRadius: 16, padding: 20, textAlign: "center",
              }}>
                <div style={{ fontSize: "0.65rem", color: "#71717a", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
                  Extra sats · last {periodLabel}
                </div>
                <div style={{ fontSize: "3.5rem", fontWeight: 800, color: "#fb923c", fontVariantNumeric: "tabular-nums", marginBottom: 4 }}>
                  +{animatedExtra.toLocaleString()}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#71717a", marginBottom: 12 }}>sats gained vs flat DCA</div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.2)",
                  borderRadius: 999, padding: "6px 16px",
                }}>
                  <span style={{ color: "#fb923c", fontWeight: 700, fontSize: "0.9rem" }}>
                    +{(bestPeriod?.extra_pct ?? result.extra_pct).toFixed(2)}%
                  </span>
                  <span style={{ color: "#71717a", fontSize: "0.75rem" }}>boost vs standard DCA</span>
                </div>

                {(() => {
                  const sens = result.timing_sensitivity;
                  const sensLabel = sens?.period_days === 91 ? "3 months" : sens?.period_days === 182 ? "6 months" : sens?.period_days === 273 ? "9 months" : sens?.period_days === 365 ? "12 months" : null;
                  return sens && sensLabel ? (
                    <div style={{ fontSize: "0.72rem", color: "#57534e", marginTop: 8 }}>
                      Historically ranges from{" "}
                      <span style={{ color: "#a1a1aa" }}>+{sens.extra_pct_min.toFixed(2)}%</span>
                      {" – "}
                      <span style={{ color: "#a1a1aa" }}>+{sens.extra_pct_max.toFixed(2)}%</span>
                      {" "}over {sensLabel}
                    </div>
                  ) : null;
                })()}

                {addedToTotal && (
                  <div style={{
                    marginTop: 14, padding: "8px 14px",
                    background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.15)",
                    borderRadius: 8, fontSize: "0.72rem", color: "#a16207",
                  }}>
                    ✓ Added to the Bitcoin 2026 total
                  </div>
                )}
              </div>

              {/* App store CTA */}
              <div style={{
                background: "#18181b", border: "1px solid #3f3f46",
                borderRadius: 16, padding: 20, textAlign: "center",
              }}>
                <p style={{ fontSize: "1rem", fontWeight: 800, marginBottom: 6 }}>
                  Start stacking those extra sats
                </p>
                <p style={{ fontSize: "0.8rem", color: "#71717a", marginBottom: 16 }}>
                  Download SatStacker — first month of Smart Timing free.
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <a
                    href="https://apps.apple.com/app/satstacker/id6739804937"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: 1, padding: "12px", borderRadius: 10,
                      background: "#f97316", color: "#fff",
                      fontWeight: 700, fontSize: "0.85rem",
                      textDecoration: "none", textAlign: "center",
                      display: "block",
                    }}
                  >
                    App Store
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=app.satstacker.satstacker"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: 1, padding: "12px", borderRadius: 10,
                      background: "#f97316", color: "#fff",
                      fontWeight: 700, fontSize: "0.85rem",
                      textDecoration: "none", textAlign: "center",
                      display: "block",
                    }}
                  >
                    Google Play
                  </a>
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <StatCard
                  label={`Avg extra / ${cadence === "bi-weekly" ? "2 wks" : cadence === "weekly" ? "week" : "day"}`}
                  value={`+${bestPeriodStats.avgExtraPerWindow.toLocaleString()}`}
                  sub="sats"
                  highlight={false}
                />
                <StatCard
                  label="Windows simulated"
                  value={bestPeriodStats.numWindows}
                  sub="buys"
                  highlight={false}
                />
                <StatCard
                  label="Baseline (flat DCA)"
                  value={bestPeriodStats.baselineSats.toLocaleString()}
                  sub="sats"
                  highlight={false}
                />
                <StatCard
                  label="With Smart Timing"
                  value={bestPeriodStats.smartSats.toLocaleString()}
                  sub="sats"
                  highlight={true}
                />
              </div>

              <TrancheBreakdown windows={result.windows} />

              <p style={{ fontSize: "0.65rem", color: "#3f3f46", lineHeight: 1.6, padding: "0 4px" }}>
                Simulation uses real hourly price data with actual Smart Timing tranche logic. Past performance doesn't guarantee future results.
              </p>

              {/* Reset button */}
              <button
                type="button"
                onClick={onReset}
                style={{
                  width: "100%", padding: "14px", borderRadius: 12,
                  background: "transparent", border: "1px solid #3f3f46",
                  color: "#a1a1aa", fontWeight: 600, fontSize: "0.9rem",
                  cursor: "pointer", marginTop: 4, transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.target.style.borderColor = "#71717a"; e.target.style.color = "#fff"; }}
                onMouseLeave={e => { e.target.style.borderColor = "#3f3f46"; e.target.style.color = "#a1a1aa"; }}
              >
                ↩ Next person
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}